import {Component, OnInit} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';

import {Severity, ToastService} from '../../../../shared/components/toast/toast.service';
import {PackageRenewalService} from './package-renewal.service';

@Component({
  selector: 'app-package-renewal',
  templateUrl: './package-renewal.component.html',
  styleUrls: ['./package-renewal.component.scss']
})
export class PackageRenewalComponent implements OnInit {
  visible = false;
  loading = false;
  submitting = false;
  changingPackage = false;
  suppressToday = false;
  thresholdWarningVisible = false;

  status: any;
  products: Array<any> = [];
  selectedProduct: any;
  checkout: any;

  contactForm = this.fb.group({
    emailAddress: ['', [Validators.required, Validators.email]],
    contactNumber: ['', Validators.required]
  });

  constructor(
    private fb: FormBuilder,
    private service: PackageRenewalService,
    private toastService: ToastService
  ) {
  }

  get checkoutQrCodeSrc(): string {
    const qrCode = this.checkout?.qrCodeBase64;
    if (!qrCode) {
      return '';
    }
    return qrCode.startsWith('data:image/') ? qrCode : `data:image/png;base64,${qrCode}`;
  }

  ngOnInit(): void {
    this.refreshStatus(true);
  }

  open(): void {
    this.visible = true;
    this.thresholdWarningVisible = false;
    this.suppressToday = false;
    this.resetContactForm();
    this.refreshStatus(false);
    this.loadProducts();
  }

  close(): void {
    if (this.thresholdWarningVisible && this.suppressToday && this.status?.customerPartyId) {
      localStorage.setItem(this.dismissKey(this.status.customerPartyId), this.todayKey());
    }
    this.visible = false;
    this.thresholdWarningVisible = false;
  }

  selectProduct(product: any): void {
    if (!product?.productId || this.changingPackage || this.checkout || !this.status?.configReady) {
      return;
    }
    this.selectedProduct = product;
  }

  createCheckout(): void {
    if (!this.selectedProduct?.productId || this.changingPackage || this.checkout || !this.status?.configReady) {
      return;
    }
    const product = this.selectedProduct;
    this.changingPackage = true;
    this.service.createCheckout(product.productId).subscribe({
      next: response => {
        this.checkout = {
          ...response,
          productId: product.productId,
          productName: product.productName,
          productPseudoId: product.pseudoId,
          packageQuantity: product.amountFixed
        };
        this.changingPackage = false;
      },
      error: () => {
        this.changingPackage = false;
        this.toastService.error();
      }
    });
  }

  copyText(value: any, label: string): void {
    const text = value == null ? '' : String(value).trim();
    if (!text) {
      return;
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => this.notifyCopied(label))
        .catch(() => this.copyTextFallback(text, label));
      return;
    }
    this.copyTextFallback(text, label);
  }

  reportPayment(): void {
    if (!this.checkout?.paymentId || this.submitting) {
      return;
    }
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }
    this.submitting = true;
    this.service.reportPayment(this.checkout.paymentId, {
      emailAddress: this.contactForm.get('emailAddress')?.value || '',
      contactNumber: this.contactForm.get('contactNumber')?.value || ''
    }).subscribe({
      next: response => {
        if (response?.expired) {
          this.checkout = null;
          this.selectedProduct = null;
          this.resetContactForm();
          this.submitting = false;
          this.refreshStatus(false);
          this.toastService.setMessage({
            severity: Severity.WARN,
            detail: 'Mã QR đã hết hạn. Vui lòng chọn gói và tạo lại mã thanh toán.'
          });
          return;
        }
        this.checkout.awaitingApproval = true;
        this.checkout.statusId = 'PmntAuthorized';
        this.submitting = false;
        this.toastService.setMessage({
          severity: Severity.SUCCESS,
          detail: 'Đã ghi nhận thanh toán. Yêu cầu đang chờ xác nhận.'
        });
      },
      error: () => {
        this.submitting = false;
        this.toastService.error();
      }
    });
  }

  cancelPayment(closeAfterCancel = false): void {
    if (!this.checkout?.paymentId || this.submitting || this.checkout.awaitingApproval) {
      return;
    }
    this.submitting = true;
    this.service.cancelPayment(this.checkout.paymentId).subscribe({
      next: () => {
        this.submitting = false;
        this.checkout = null;
        this.selectedProduct = null;
        this.resetContactForm();
        if (this.status) {
          this.status.activeCheckout = null;
        }
        if (closeAfterCancel) {
          this.close();
        } else {
          this.toastService.success();
        }
      },
      error: () => {
        this.submitting = false;
        this.toastService.error();
      }
    });
  }

  exit(): void {
    if (this.submitting || this.changingPackage) {
      return;
    }
    if (this.checkout?.paymentId && !this.checkout.awaitingApproval) {
      this.cancelPayment(true);
      return;
    }
    this.close();
  }

  private refreshStatus(autoOpen: boolean): void {
    this.loading = true;
    this.service.getStatus().subscribe({
      next: status => {
        this.status = status;
        this.checkout = status?.activeCheckout || null;
        this.loading = false;
        if (autoOpen && status?.shouldWarn && !this.dismissedToday(status.customerPartyId)) {
          this.thresholdWarningVisible = true;
          this.visible = true;
          this.loadProducts();
        }
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private loadProducts(): void {
    if (this.products.length) {
      this.restoreSelectedProduct();
      return;
    }
    this.service.getProducts().subscribe({
      next: response => {
        this.products = response?.listData || [];
        this.restoreSelectedProduct();
      },
      error: () => this.toastService.error()
    });
  }

  private restoreSelectedProduct(): void {
    if (this.checkout?.productId) {
      this.selectedProduct = this.products.find(item => item.productId === this.checkout.productId);
    }
  }

  private resetContactForm(): void {
    this.contactForm.reset({
      emailAddress: '',
      contactNumber: ''
    });
  }

  private copyTextFallback(text: string, label: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      if (document.execCommand('copy')) {
        this.notifyCopied(label);
      } else {
        this.toastService.error();
      }
    } catch {
      this.toastService.error();
    } finally {
      document.body.removeChild(textArea);
    }
  }

  private notifyCopied(label: string): void {
    this.toastService.setMessage({
      severity: Severity.SUCCESS,
      detail: `Đã sao chép ${label}.`
    });
  }

  private dismissedToday(customerPartyId: string): boolean {
    if (!customerPartyId) {
      return false;
    }
    return localStorage.getItem(this.dismissKey(customerPartyId)) === this.todayKey();
  }

  private dismissKey(customerPartyId: string): string {
    return `package-renewal-warning:${customerPartyId}`;
  }

  private todayKey(): string {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  }
}
